<template>
  <div class="relative flex-1 min-h-0">
    <div
      ref="containerRef"
      class="flex h-full min-h-0 flex-col gap-5 overflow-y-auto p-[15px]"
      @scroll.passive="handleScroll"
    >
      <p
        v-if="loading"
        class="font-helvetica-400 font-normal text-[16px] text-extra-text-color text-center"
      >
        Загружаем переписку...
      </p>
      <p
        v-else-if="errorText"
        class="font-helvetica-400 font-normal text-[16px] text-danger text-center"
      >
        {{ errorText }}
      </p>

      <template v-else>
        <template
          v-for="(message, index) in resolvedMessages"
          :key="message.key"
        >
          <p
            v-if="shouldRenderDateLabel(index)"
            class="font-helvetica-400 font-normal text-[16px] text-extra-text-color text-center"
          >
            {{ formatDateLabel(message.createAt) }}
          </p>

          <div v-if="message.fromSystem" class="flex justify-center">
            <div
              class="max-w-[90%] text-center font-helvetica-400 font-normal text-[14px] text-extra-text-color"
            >
              <div
                v-if="hasTextContent(message)"
                v-html="renderMessageHtml(message)"
              ></div>
              <ChatAttachment
                v-if="message.file"
                class="mx-auto mt-2"
                :file="message.file"
                variant="system"
              />
            </div>
          </div>

          <div
            v-else-if="message.fromClient"
            class="flex gap-[15px] items-center justify-end"
          >
            <p
              class="font-helvetica-400 font-normal text-[14px] text-extra-text-color"
            >
              {{ formatTime(message.createAt) }}
            </p>
            <div
              class="bg-green-button max-w-[80%] text-white-gray p-3.5 rounded-tl-[10px] rounded-tr-[10px] rounded-bl-[10px] whitespace-pre-wrap break-words"
            >
              <div
                v-if="hasTextContent(message)"
                v-html="renderMessageHtml(message)"
              ></div>
              <ChatAttachment
                v-if="message.file"
                class="mt-2"
                :file="message.file"
                variant="outgoing"
              />
            </div>
          </div>

          <div v-else class="flex gap-[15px] items-center justify-start">
            <div
              class="bg-chat-msg-box max-w-[80%] text-gray-dark p-3.5 rounded-tl-[10px] rounded-tr-[10px] rounded-br-[10px] whitespace-pre-wrap break-words"
            >
              <div
                v-if="hasTextContent(message)"
                v-html="renderMessageHtml(message)"
              ></div>
              <ChatAttachment
                v-if="message.file"
                class="mt-2"
                :file="message.file"
                variant="incoming"
              />
            </div>
            <p
              class="font-helvetica-400 font-normal text-[14px] text-extra-text-color"
            >
              {{ formatTime(message.createAt) }}
            </p>
          </div>
        </template>
      </template>
    </div>

    <button
      v-if="showScrollToBottomButton && resolvedMessages.length"
      type="button"
      class="shadow-fab-orange absolute bottom-4 right-4 z-10 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-orange text-white transition-transform duration-200 hover:scale-[1.04] hover:bg-selected-orange"
      aria-label="Прокрутить чат вниз"
      @click="handleScrollToBottomClick"
    >
      <svg
        class="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 5V19M12 19L6.75 13.75M12 19L17.25 13.75"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from "vue";
import type { HelpChatUiMessage } from "~/types/helpChat";

const props = withDefaults(
  defineProps<{
    messages?: HelpChatUiMessage[];
    loading?: boolean;
    errorText?: string;
    highlightQuery?: string;
    highlightMessageIds?: number[];
  }>(),
  {
    messages: () => [],
    loading: false,
    errorText: "",
    highlightQuery: "",
    highlightMessageIds: () => [],
  },
);

const containerRef = ref<HTMLElement | null>(null);
const showScrollToBottomButton = ref(false);
const AUTO_SCROLL_THRESHOLD_PX = 80;
const SHOW_SCROLL_BUTTON_THRESHOLD_PX = 120;

const resolvedMessages = computed<HelpChatUiMessage[]>(
  () => props.messages ?? [],
);
const normalizedQuery = computed(() =>
  String(props.highlightQuery ?? "").trim(),
);
const highlightedIdSet = computed(
  () =>
    new Set(
      (props.highlightMessageIds ?? []).filter((id) => Number.isFinite(id)),
    ),
);

const parseDate = (value: string) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;

  const direct = new Date(normalized);
  if (!Number.isNaN(direct.getTime())) return direct;

  const legacy = normalized.match(
    /^(\d{2})\.(\d{2})\.(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (!legacy) return null;

  const [, day, month, year, hour, minute, second = "0"] = legacy;
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  );
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const toDateKey = (value: string) => {
  const date = parseDate(value);
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateLabel = (value: string) => {
  const date = parseDate(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const formatTime = (value: string) => {
  const date = parseDate(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const hasTextContent = (message: HelpChatUiMessage) =>
  String(message.text ?? "").trim().length > 0;

const renderMessageHtml = (message: HelpChatUiMessage) => {
  const raw = String(message.text ?? "");
  const escapedText = escapeHtml(raw).replace(/\n/g, "<br/>");
  const query = normalizedQuery.value;
  if (!query) return escapedText;
  if (typeof message.serverId !== "number") return escapedText;
  if (!highlightedIdSet.value.has(message.serverId)) return escapedText;

  const pattern = escapeRegExp(query);
  const regex = new RegExp(pattern, "gi");
  return escapedText.replace(
    regex,
    (matched) => `<mark class="chat-search-mark">${matched}</mark>`,
  );
};

const shouldRenderDateLabel = (index: number) => {
  const currentMessage = resolvedMessages.value[index];
  if (!currentMessage) return false;

  const currentDateKey = toDateKey(currentMessage.createAt);
  if (!currentDateKey) return false;
  if (index === 0) return true;

  const previousMessage = resolvedMessages.value[index - 1];
  if (!previousMessage) return true;

  const previousDateKey = toDateKey(previousMessage.createAt);
  return previousDateKey !== currentDateKey;
};

const getDistanceFromBottom = (container?: HTMLElement | null) => {
  if (!container) return 0;

  return Math.max(
    container.scrollHeight - container.scrollTop - container.clientHeight,
    0,
  );
};

const isNearBottom = (container?: HTMLElement | null) => {
  if (!container) return true;
  return getDistanceFromBottom(container) <= AUTO_SCROLL_THRESHOLD_PX;
};

const syncScrollState = () => {
  const container = containerRef.value;
  if (!container) {
    showScrollToBottomButton.value = false;
    return;
  }

  showScrollToBottomButton.value =
    getDistanceFromBottom(container) > SHOW_SCROLL_BUTTON_THRESHOLD_PX;
};

const scrollToBottom = (behavior: ScrollBehavior = "auto") => {
  const container = containerRef.value;
  if (!container) return;

  container.scrollTo({
    top: container.scrollHeight,
    behavior,
  });
  showScrollToBottomButton.value = false;
};

const handleScroll = () => {
  syncScrollState();
};

const handleScrollToBottomClick = () => {
  scrollToBottom("smooth");
};

watch(
  () => resolvedMessages.value.length,
  () => {
    const shouldAutoScroll = isNearBottom(containerRef.value);

    nextTick(() => {
      if (shouldAutoScroll) {
        scrollToBottom();
        return;
      }

      syncScrollState();
    });
  },
  { immediate: true },
);

watch(
  () => props.loading,
  (loading) => {
    if (loading) return;
    nextTick(() => {
      scrollToBottom();
    });
  },
);
</script>

<style scoped>
:deep(.chat-search-mark) {
  background: var(--color-selected-orange);
  color: var(--color-white);
  border-radius: 2px;
  padding: 0 2px;
}
</style>
